import { IInfoCategoryMasterData } from '@Domains/MasterData';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DEFAULT_CHECKED_SUBCOMMITTEE_VALUE } from './utils';

type CheckboxListProps = {
  name: string;
  filteredSubcommittees: IInfoCategoryMasterData[];
  selectedSubcommittees: string[];
  disabled?: boolean;
  mode: 'create' | 'edit';
  onCheckboxChange: (nameTh: string, checked: boolean) => void;
};

const FormCheckboxList = ({
  name,
  filteredSubcommittees,
  selectedSubcommittees,
  disabled = false,
  mode,
  onCheckboxChange,
}: CheckboxListProps) => {
  const { getValues, setValue } = useFormContext();
  const [selected, setSelected] = useState<string[]>(selectedSubcommittees);
  const filterSubcommittees = filteredSubcommittees.some(
    (sub) => sub.nameEn === DEFAULT_CHECKED_SUBCOMMITTEE_VALUE.nameEn
  );

  const subcommittees = getValues(name);
  const isCheckedDefault = useRef(false);
  const isInitialCreateMode =
    subcommittees.length === 0 &&
    !isCheckedDefault.current &&
    mode === 'create';

  useEffect(() => {
    if (filterSubcommittees) {
      if (isInitialCreateMode) {
        setValue(name, [DEFAULT_CHECKED_SUBCOMMITTEE_VALUE]);
        setSelected([DEFAULT_CHECKED_SUBCOMMITTEE_VALUE.nameTh]);
        isCheckedDefault.current = true;
      }
    } else {
      setSelected(
        subcommittees.map(
          (subcommittee: IInfoCategoryMasterData) => subcommittee.nameTh
        )
      );
    }
  }, [getValues, setValue, name, filterSubcommittees, subcommittees]);

  useEffect(() => {
    setSelected((prevSelected) => {
      const newSelected = new Set(prevSelected);

      if (filterSubcommittees) {
        if (isInitialCreateMode) {
          newSelected.add(DEFAULT_CHECKED_SUBCOMMITTEE_VALUE.nameTh);
          newSelected.add(DEFAULT_CHECKED_SUBCOMMITTEE_VALUE.nameEn);
          isCheckedDefault.current = true;
        }
      }

      return Array.from(newSelected);
    });
  }, [
    selectedSubcommittees,
    filteredSubcommittees,
    filterSubcommittees,
    subcommittees.length,
  ]);

  useEffect(() => {
    setSelected(selectedSubcommittees);
  }, [selectedSubcommittees]);

  const handleCheckboxChange = (nameTh: string, checked: boolean) => {
    onCheckboxChange(nameTh, checked);
    if (!checked || nameTh === DEFAULT_CHECKED_SUBCOMMITTEE_VALUE.nameTh) {
      isCheckedDefault.current = true;
    }
  };

  return (
    <Box
      maxHeight="250px"
      p={1}
      border="1px solid rgba(0, 0, 0, 0.12)"
      borderTop="none"
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
      }}
    >
      {filteredSubcommittees.map((option) => (
        <FormControlLabel
          key={option.id}
          control={
            <Checkbox
              disabled={disabled}
              checked={selected.includes(option.nameTh)}
              onChange={(e) =>
                handleCheckboxChange(option.nameTh, e.target.checked)
              }
            />
          }
          label={option.nameTh}
          sx={{ width: '100%' }}
        />
      ))}
    </Box>
  );
};

export default FormCheckboxList;
